import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";

export interface ContractsThisYearResponse {
  contracts: any[];
  count: number;
  totalValue: number;
  contractDetails: any[];
}

/**
 * Shared, deduped fetcher for the expensive `/rental/metrics/contracts-created-this-year`
 * endpoint.
 *
 * Two hooks (useRentalMetrics, useContractsThisYear) and unstable Clerk `session`
 * references previously caused this slow endpoint to be hit multiple times within the
 * same second, which contributed to the backend being marked "unhealthy" by Render.
 *
 * This module collapses concurrent calls into a single in-flight request and caches the
 * result for a short TTL so re-renders don't re-trigger the query.
 */
const TTL_MS = 30_000;

let inFlight: Promise<ContractsThisYearResponse> | null = null;
let cache: { data: ContractsThisYearResponse; ts: number } | null = null;

export const fetchContractsCreatedThisYear = (
  session: any
): Promise<ContractsThisYearResponse> => {
  const now = Date.now();

  // Serve from short-lived cache when fresh.
  if (cache && now - cache.ts < TTL_MS) {
    return Promise.resolve(cache.data);
  }

  // Collapse concurrent callers onto a single request.
  if (inFlight) {
    return inFlight;
  }

  inFlight = (async () => {
    try {
      const response = await fetchWithAuth(
        `${API_URL}/rental/metrics/contracts-created-this-year`,
        { method: "GET" },
        session
      );

      if (!response.ok) {
        throw new Error(
          `contracts-created-this-year request failed with status ${response.status}`
        );
      }

      const data: ContractsThisYearResponse = await response.json();
      cache = { data, ts: Date.now() };
      return data;
    } finally {
      // Always release the in-flight slot so failures don't wedge future calls.
      inFlight = null;
    }
  })();

  return inFlight;
};

/** Clears the cache so the next call re-fetches (use after mutations). */
export const invalidateContractsCreatedThisYear = (): void => {
  cache = null;
};
